import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';

interface ProfileData {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  marketingConsent: boolean;
}

interface PasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const EditProfilePage: React.FC = () => {
  const { user, fetchProfile, logout } = useAuth();
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [profileData, setProfileData] = useState<ProfileData>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    marketingConsent: false
  });

  const [passwordData, setPasswordData] = useState<PasswordData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber: user.phoneNumber || '',
        marketingConsent: user.marketingConsent || false
      });
    }
  }, [user]);

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setProfileData({
      ...profileData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value
    });
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    setLoading(true);

    try {
      await api.put('/auth/profile', profileData);
      await fetchProfile(); // Refresh user data
      setSuccess(t('profile.profileUpdated'));
    } catch (err: any) {
      setError(err.response?.data?.error || t('profile.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError(t('auth.passwordsDoNotMatch'));
      return;
    }

    setPasswordLoading(true);

    try {
      await api.put('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      setPasswordSuccess(t('profile.passwordChanged'));
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      // Close dialog after successful password change
      setTimeout(() => {
        setPasswordDialogOpen(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (err: any) {
      setPasswordError(err.response?.data?.error || t('profile.passwordChangeFailed'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    setError('');

    try {
      await api.delete('/auth/account');
      // Account deleted successfully, logout and redirect to home
      await logout();
      window.location.href = '/';
    } catch (err: any) {
      setError(err.response?.data?.error || t('profile.deleteAccountFailed'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setError('');
  };

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        maxWidth: 600, 
        margin: '0 auto', 
        padding: 3,
        direction: currentLang === 'he' ? 'rtl' : 'ltr'
      }}
    >
      <Typography variant="h4" component="h1" gutterBottom align="center">
        {t('profile.editProfile')}
      </Typography>

      <Paper elevation={2}>
        <form onSubmit={handleProfileSubmit}>
              <List sx={{ direction: currentLang === 'he' ? 'rtl' : 'ltr' }}>
                {/* Email - Read Only */}
                <ListItem sx={{ flexDirection: currentLang === 'he' ? 'row-reverse' : 'row' }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 'fit-content',
                    marginRight: currentLang === 'he' ? 0 : 'auto',
                    marginLeft: currentLang === 'he' ? 'auto' : 0,
                    paddingLeft: currentLang === 'he' ? 0 : 2,
                    paddingRight: currentLang === 'he' ? 2 : 0
                  }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textAlign: currentLang === 'he' ? 'right' : 'left' }}
                    >
                      {t('profile.cannotChange')}
                    </Typography>
                  </Box>
                  <ListItemText
                    primary={t('auth.email')}
                    secondary={user.email}
                    sx={{
                      textAlign: currentLang === 'he' ? 'right' : 'left',
                      paddingRight: currentLang === 'he' ? 2 : 0,
                      paddingLeft: currentLang === 'he' ? 0 : 2,
                      flex: 1,
                      '& .MuiListItemText-primary': {
                        textAlign: currentLang === 'he' ? 'right' : 'left'
                      },
                      '& .MuiListItemText-secondary': {
                        textAlign: currentLang === 'he' ? 'right' : 'left'
                      }
                    }}
                  />
                </ListItem>

                {/* First Name - Editable */}
                <ListItem sx={{ flexDirection: currentLang === 'he' ? 'row-reverse' : 'row' }}>
                  <ListItemText
                    primary={t('auth.firstName')}
                    secondary={
                      <TextField
                        fullWidth
                        name="firstName"
                        type="text"
                        value={profileData.firstName}
                        onChange={handleProfileChange}
                        disabled={loading}
                        required
                        variant="outlined"
                        size="small"
                        sx={{
                          mt: 1,
                          '& .MuiInputBase-input': {
                            textAlign: currentLang === 'he' ? 'right' : 'left'
                          }
                        }}
                      />
                    }
                    sx={{
                      textAlign: currentLang === 'he' ? 'right' : 'left',
                      paddingRight: currentLang === 'he' ? 0 : 0,
                      paddingLeft: currentLang === 'he' ? 0 : 0,
                      flex: 1,
                      '& .MuiListItemText-primary': {
                        textAlign: currentLang === 'he' ? 'right' : 'left'
                      }
                    }}
                  />
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 'fit-content',
                    marginLeft: currentLang === 'he' ? 0 : 'auto',
                    marginRight: currentLang === 'he' ? 'auto' : 0,
                    paddingLeft: currentLang === 'he' ? 0 : 2,
                    paddingRight: currentLang === 'he' ? 2 : 0
                  }}>
                    {/* Empty space for alignment */}
                  </Box>
                </ListItem>

                {/* Last Name - Editable */}
                <ListItem sx={{ flexDirection: currentLang === 'he' ? 'row-reverse' : 'row' }}>
                  <ListItemText
                    primary={t('auth.lastName')}
                    secondary={
                      <TextField
                        fullWidth
                        name="lastName"
                        type="text"
                        value={profileData.lastName}
                        onChange={handleProfileChange}
                        disabled={loading}
                        required
                        variant="outlined"
                        size="small"
                        sx={{
                          mt: 1,
                          '& .MuiInputBase-input': {
                            textAlign: currentLang === 'he' ? 'right' : 'left'
                          }
                        }}
                      />
                    }
                    sx={{
                      textAlign: currentLang === 'he' ? 'right' : 'left',
                      paddingRight: currentLang === 'he' ? 0 : 0,
                      paddingLeft: currentLang === 'he' ? 0 : 0,
                      flex: 1,
                      '& .MuiListItemText-primary': {
                        textAlign: currentLang === 'he' ? 'right' : 'left'
                      }
                    }}
                  />
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 'fit-content',
                    marginLeft: currentLang === 'he' ? 0 : 'auto',
                    marginRight: currentLang === 'he' ? 'auto' : 0,
                    paddingLeft: currentLang === 'he' ? 0 : 2,
                    paddingRight: currentLang === 'he' ? 2 : 0
                  }}>
                    {/* Empty space for alignment */}
                  </Box>
                </ListItem>

            {/* Phone Number - Editable */}
            <ListItem sx={{ flexDirection: currentLang === 'he' ? 'row-reverse' : 'row' }}>
              <ListItemText
                primary={t('auth.phoneNumber')}
                secondary={
                  <TextField
                    fullWidth
                    name="phoneNumber"
                    type="tel"
                    value={profileData.phoneNumber || ''}
                    onChange={handleProfileChange}
                    disabled={loading}
                    placeholder={t('auth.phoneNumberOptional')}
                    helperText={t('auth.phoneNumberOptional')}
                    variant="outlined"
                    size="small"
                    sx={{ 
                      mt: 1,
                      '& .MuiInputBase-input': {
                        textAlign: currentLang === 'he' ? 'right' : 'left'
                      }
                    }}
                  />
                }
                sx={{ 
                  textAlign: currentLang === 'he' ? 'right' : 'left',
                  paddingRight: currentLang === 'he' ? 0 : 0,
                  paddingLeft: currentLang === 'he' ? 0 : 0,
                  flex: 1,
                  '& .MuiListItemText-primary': {
                    textAlign: currentLang === 'he' ? 'right' : 'left'
                  }
                }}
              />
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                minWidth: 'fit-content',
                marginLeft: currentLang === 'he' ? 0 : 'auto',
                marginRight: currentLang === 'he' ? 'auto' : 0,
                paddingLeft: currentLang === 'he' ? 0 : 2,
                paddingRight: currentLang === 'he' ? 2 : 0
              }}>
                {/* Empty space for alignment */}
              </Box>
            </ListItem>

            {/* Marketing Consent - Editable */}
            <ListItem sx={{ flexDirection: currentLang === 'he' ? 'row-reverse' : 'row' }}>
              <ListItemText
                primary={t('auth.marketingConsent')}
                secondary={t('auth.marketingConsentOptional')}
                sx={{ 
                  textAlign: currentLang === 'he' ? 'right' : 'left',
                  paddingRight: currentLang === 'he' ? 2 : 0,
                  paddingLeft: currentLang === 'he' ? 0 : 2,
                  flex: 1,
                  '& .MuiListItemText-primary': {
                    textAlign: currentLang === 'he' ? 'right' : 'left'
                  },
                  '& .MuiListItemText-secondary': {
                    textAlign: currentLang === 'he' ? 'right' : 'left'
                  }
                }}
              />
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                minWidth: 'fit-content',
                marginLeft: currentLang === 'he' ? 0 : 'auto',
                marginRight: currentLang === 'he' ? 'auto' : 0,
                paddingLeft: currentLang === 'he' ? 0 : 2,
                paddingRight: currentLang === 'he' ? 2 : 0
              }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={profileData.marketingConsent}
                      onChange={handleProfileChange}
                      name="marketingConsent"
                      disabled={loading}
                      color="primary"
                    />
                  }
                  label=""
                  sx={{
                    marginRight: 0,
                    '& .MuiFormControlLabel-root': {
                      marginRight: 0
                    }
                  }}
                />
              </Box>
            </ListItem>

                {/* Change Password Button */}
                <ListItem sx={{ flexDirection: currentLang === 'he' ? 'row-reverse' : 'row' }}>
                  <ListItemText
                    primary={t('profile.changePassword')}
                    secondary={t('profile.clickToChangePassword')}
                    sx={{
                      textAlign: currentLang === 'he' ? 'right' : 'left',
                      paddingRight: currentLang === 'he' ? 2 : 0,
                      paddingLeft: currentLang === 'he' ? 0 : 2,
                      flex: 1,
                      '& .MuiListItemText-primary': {
                        textAlign: currentLang === 'he' ? 'right' : 'left'
                      },
                      '& .MuiListItemText-secondary': {
                        textAlign: currentLang === 'he' ? 'right' : 'left'
                      }
                    }}
                  />
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 'fit-content',
                    marginLeft: currentLang === 'he' ? 0 : 'auto',
                    marginRight: currentLang === 'he' ? 'auto' : 0,
                    paddingLeft: currentLang === 'he' ? 0 : 2,
                    paddingRight: currentLang === 'he' ? 2 : 0
                  }}>
                    <IconButton
                      onClick={() => setPasswordDialogOpen(true)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  </Box>
                </ListItem>

                {/* Delete Account Button */}
                <ListItem sx={{ flexDirection: currentLang === 'he' ? 'row-reverse' : 'row' }}>
                  <ListItemText
                    primary={t('profile.deleteAccount')}
                    secondary={t('profile.deleteAccountWarning')}
                    sx={{
                      textAlign: currentLang === 'he' ? 'right' : 'left',
                      paddingRight: currentLang === 'he' ? 2 : 0,
                      paddingLeft: currentLang === 'he' ? 0 : 2,
                      flex: 1,
                      '& .MuiListItemText-primary': {
                        textAlign: currentLang === 'he' ? 'right' : 'left',
                        color: 'error.main'
                      },
                      '& .MuiListItemText-secondary': {
                        textAlign: currentLang === 'he' ? 'right' : 'left',
                        color: 'error.main'
                      }
                    }}
                  />
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 'fit-content',
                    marginLeft: currentLang === 'he' ? 0 : 'auto',
                    marginRight: currentLang === 'he' ? 'auto' : 0,
                    paddingLeft: currentLang === 'he' ? 0 : 2,
                    paddingRight: currentLang === 'he' ? 2 : 0
                  }}>
                    <IconButton
                      onClick={() => setDeleteDialogOpen(true)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </ListItem>
          </List>

          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ m: 2 }}>
              {success}
            </Alert>
          )}

          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              size="large"
            >
              {loading ? <CircularProgress size={24} /> : t('profile.updateProfile')}
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Password Change Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onClose={handlePasswordDialogClose}
        maxWidth="sm"
        fullWidth
        sx={{ direction: currentLang === 'he' ? 'rtl' : 'ltr' }}
      >
        <DialogTitle sx={{ textAlign: currentLang === 'he' ? 'right' : 'left' }}>
          {t('profile.changePassword')}
        </DialogTitle>
        <form onSubmit={handlePasswordSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label={t('profile.currentPassword')}
              name="currentPassword"
              type="password"
              value={passwordData.currentPassword}
              onChange={handlePasswordChange}
              required
              disabled={passwordLoading}
              margin="normal"
              sx={{
                '& .MuiInputBase-input': {
                  textAlign: currentLang === 'he' ? 'right' : 'left'
                },
                '& .MuiInputLabel-root': {
                  textAlign: currentLang === 'he' ? 'right' : 'left'
                }
              }}
            />
            <TextField
              fullWidth
              label={t('profile.newPassword')}
              name="newPassword"
              type="password"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              required
              disabled={passwordLoading}
              margin="normal"
              sx={{
                '& .MuiInputBase-input': {
                  textAlign: currentLang === 'he' ? 'right' : 'left'
                },
                '& .MuiInputLabel-root': {
                  textAlign: currentLang === 'he' ? 'right' : 'left'
                }
              }}
            />
            <TextField
              fullWidth
              label={t('profile.confirmNewPassword')}
              name="confirmPassword"
              type="password"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              required
              disabled={passwordLoading}
              margin="normal"
              sx={{
                '& .MuiInputBase-input': {
                  textAlign: currentLang === 'he' ? 'right' : 'left'
                },
                '& .MuiInputLabel-root': {
                  textAlign: currentLang === 'he' ? 'right' : 'left'
                }
              }}
            />

            {passwordError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {passwordError}
              </Alert>
            )}

            {passwordSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {passwordSuccess}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handlePasswordDialogClose} disabled={passwordLoading}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={passwordLoading}
            >
              {passwordLoading ? <CircularProgress size={24} /> : t('profile.changePassword')}
            </Button>
          </DialogActions>
        </form>
          </Dialog>

          {/* Delete Account Confirmation Dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={handleDeleteDialogClose}
            maxWidth="sm"
            fullWidth
            sx={{ direction: currentLang === 'he' ? 'rtl' : 'ltr' }}
          >
            <DialogTitle sx={{ textAlign: currentLang === 'he' ? 'right' : 'left', color: 'error.main' }}>
              {t('profile.deleteAccount')}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 2, textAlign: currentLang === 'he' ? 'right' : 'left' }}>
                {t('profile.deleteAccountConfirm')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, textAlign: currentLang === 'he' ? 'right' : 'left', fontWeight: 'bold' }}>
                {t('auth.email')}: {user?.email}
              </Typography>
              <Alert severity="error" sx={{ textAlign: currentLang === 'he' ? 'right' : 'left' }}>
                {t('profile.deleteAccountWarning')}
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDeleteDialogClose} disabled={deleteLoading}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleDeleteAccount}
                variant="contained"
                color="error"
                disabled={deleteLoading}
              >
                {deleteLoading ? <CircularProgress size={24} /> : t('profile.deleteAccount')}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      );
    };
